/**
 * Bulk-import spec for the `Notifications` sheet of the organizer workbook.
 *
 * One row per notification. The bulk callable resolves `audience` (an
 * audience name reference for this event) and converts the event-local
 * `scheduledAt` string into a UTC Timestamp admin-side before sending.
 *
 * `scheduleMode` semantics:
 *   - `now`       -> server writes `status: 'sent'`; sendNotification CF
 *                    fires immediately on the audience.
 *   - `scheduled` -> server writes `status: 'scheduled'` with a
 *                    `scheduledAt`; the processScheduledNotifications cron
 *                    flips status to 'sent' at the scheduled time.
 *
 * `channels` is a comma-list (e.g. `push,email`). At least one channel
 * is required. `deepLink` is free-text — the admin compose form treats
 * the same way (verbatim into the notification doc).
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { normalize } from "./importTypes";

export type NotificationChannelValue = "push" | "email";
export type NotificationScheduleMode = "now" | "scheduled";

const ALLOWED_CHANNELS: readonly NotificationChannelValue[] = [
  "push",
  "email",
] as const;

const ALLOWED_SCHEDULE_MODES: readonly NotificationScheduleMode[] = [
  "now",
  "scheduled",
] as const;

/**
 * Accept the operator's two synonyms for "future send" — the workbook
 * uses `later`, the admin compose form uses `scheduled`. We canonicalize
 * to `scheduled` (matches the notification doc's `status` value).
 */
const SCHEDULE_MODE_ALIASES: Record<string, NotificationScheduleMode> = {
  now: "now",
  immediate: "now",
  scheduled: "scheduled",
  later: "scheduled",
};

/** Match "YYYY-MM-DD HH:MM" or "YYYY-MM-DDTHH:MM" (with optional seconds). */
const LOCAL_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;

function parseLocalDateTime(raw: string | undefined): string | null {
  const value = normalize(raw);
  if (!value) return null;
  const m = LOCAL_DATETIME_PATTERN.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function splitCommaList(raw: string | undefined): string[] {
  return normalize(raw)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface RawNotificationImportRow
  extends Record<string, string | undefined> {
  title?: string;
  body?: string;
  audience?: string;
  channels?: string;
  scheduleMode?: string;
  scheduledAt?: string;
  deepLink?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
}

export interface ParsedNotificationImportRow {
  title: string;
  body: string;
  /** Audience name reference (case-insensitive match against this event's audiences). */
  audience: string;
  channels: NotificationChannelValue[];
  scheduleMode: NotificationScheduleMode;
  /** Event-local naive datetime "YYYY-MM-DDTHH:mm" when scheduleMode=scheduled; null otherwise. */
  scheduledAtLocal: string | null;
  /** Free-text deep link reference (e.g. screen name or path). Stored verbatim. */
  deepLink: string | null;
  /**
   * Optional email-specific subject line. Set when the email subject
   * should differ from the push notification `title`. Null falls back
   * to `title` at send time. Ignored when `channels` doesn't include
   * `email`.
   */
  emailSubject: string | null;
  /**
   * Optional email-specific HTML body. Set when the email content
   * should be richer than the push `body`. Null falls back to the
   * plain-text `body` at send time. Ignored when `channels` doesn't
   * include `email`.
   */
  emailBodyHtml: string | null;
}

export const notificationImportSpec: ImportSpec<
  RawNotificationImportRow,
  ParsedNotificationImportRow
> = {
  sheetName: "Notifications",
  purpose:
    "Push + email notifications to send during the event. One row per notification. Audience refers to a named audience (custom group or role audience) for this event.",
  columns: [
    { key: "title", note: "required - notification headline" },
    { key: "body", note: "required - notification body text" },
    {
      key: "audience",
      note:
        "required - audience name for this event (e.g. 'All Members', 'Skiers'). Must match an existing audience name.",
    },
    {
      key: "channels",
      note: "required - comma-separated: 'push', 'email', or 'push,email'",
    },
    {
      key: "scheduleMode",
      note:
        "required - 'now' to send immediately, 'scheduled' (or 'later') for a future send",
    },
    {
      key: "scheduledAt",
      note:
        "YYYY-MM-DD HH:MM in event-local time. Required when scheduleMode=scheduled.",
    },
    {
      key: "deepLink",
      note:
        "optional - destination tap target (screen name, e.g. 'Travel', or a deep link path)",
    },
    {
      key: "emailSubject",
      note:
        "optional - email subject line when channels includes 'email'. Falls back to title.",
    },
    {
      key: "emailBodyHtml",
      note:
        "optional - HTML email body when channels includes 'email'. Falls back to body.",
    },
  ],
  example: {
    title: "Welcome to Winter Summit",
    body: "Tonight's reception starts at 7 PM in the Grand Hall.",
    audience: "All Members",
    channels: "push",
    scheduleMode: "now",
    scheduledAt: "",
    deepLink: "Event Home",
    emailSubject: "",
    emailBodyHtml: "",
  },
  identity: {
    idPrefix: "notification",
    label: (row) => row.title,
    naturalKey: (row) => [
      row.title,
      row.audience,
      row.scheduleMode,
      row.scheduledAtLocal ?? "now",
      row.channels.join(","),
    ],
  },
  validate(raw, lineNumber) {
    const title = normalize(raw.title);
    const body = normalize(raw.body);
    const audience = normalize(raw.audience);
    const channelsList = splitCommaList(raw.channels).map((c) =>
      c.toLowerCase(),
    );
    const scheduleModeRaw = normalize(raw.scheduleMode).toLowerCase();
    const scheduleMode = SCHEDULE_MODE_ALIASES[scheduleModeRaw];
    const scheduledAtLocal = parseLocalDateTime(raw.scheduledAt);
    const deepLink = normalize(raw.deepLink) || null;
    const emailSubject = normalize(raw.emailSubject) || null;
    const emailBodyHtml = normalize(raw.emailBodyHtml) || null;

    const base: Omit<
      ParsedRow<ParsedNotificationImportRow>,
      "status" | "message"
    > = {
      lineNumber,
      data: {
        title,
        body,
        audience,
        channels: channelsList as NotificationChannelValue[],
        scheduleMode: (scheduleMode ?? "now") as NotificationScheduleMode,
        scheduledAtLocal,
        deepLink,
        emailSubject,
        emailBodyHtml,
      },
    };

    if (!title) return invalid(base, "Title is required");
    if (!body) return invalid(base, "Body is required");
    if (!audience) return invalid(base, "Audience is required");

    if (channelsList.length === 0) {
      return invalid(
        base,
        "Channels is required (e.g. 'push' or 'push,email')",
      );
    }
    for (const ch of channelsList) {
      if (!(ALLOWED_CHANNELS as readonly string[]).includes(ch)) {
        return invalid(
          base,
          `Unknown channel "${ch}". Must be one of: ${ALLOWED_CHANNELS.join(", ")}`,
        );
      }
    }

    if (!scheduleMode) {
      return invalid(
        base,
        `Invalid scheduleMode "${scheduleModeRaw}". Must be one of: ${ALLOWED_SCHEDULE_MODES.join(", ")}`,
      );
    }
    if (scheduleMode === "scheduled" && !scheduledAtLocal) {
      return invalid(
        base,
        'scheduledAt must be in "YYYY-MM-DD HH:MM" format when scheduleMode=scheduled.',
      );
    }

    return pending({
      ...base,
      data: {
        ...base.data,
        channels: Array.from(
          new Set(channelsList),
        ) as NotificationChannelValue[],
      },
    });
  },
};
